import { useCallback, useMemo } from 'react'
import type { Product } from '@mc/api'
import { paths } from '@mc/paths'
import {
  trackGoldStandardPurchaseClick,
  type PurchaseClickProps,
} from '@mc/pre-paywall-events'
import { usePromotion } from '@mc/promo-utils'
import { useStaticData } from '@mc/static-data-react'
import { i18nInstance } from '../i18n'

// Note: Uses getFixedT instead of useTranslation due to SSR context issues.
// useTranslation fails during server-side rendering when React context isn't available.
const tf = i18nInstance.getFixedT(null, '@mc/growth')

// TODO(PEN-6968): rename to a quiz-specific name (e.g. useQuizNextPageHref).
// The underlying `ctaNextPage` static-data value comes exclusively from the
// quiz-only-once cookie (`quiz-next-href`) — it is NOT a general-purpose CTA
// redirect. Storing a non-quiz URL here would falsely show the "Finish Sign
// Up" copy.
export const useCookieRedirectHref = () => {
  const { ctaNextPage } = useStaticData<{
    ctaNextPage?: string
  }>()

  return useMemo(() => {
    try {
      return JSON.parse(ctaNextPage || 'null')
    } catch (e) {
      // parse error, pass
    }

    return null
  }, [ctaNextPage])
}

/**
 * Hook that provides primary CTA copy and href based on user state and quiz flow.
 *
 * Normally, CTA should redirect to class recommendations page.
 *
 * Quiz Only Once Flow:
 * - "Sign Up CTA" → (⭐Promo Page) → Quiz → Quiz Results Page → Plans page
 * - "Sign Up CTA" → (⭐Promo Page) → Quiz → Quiz Results Page → drop off → "Finish Sign Up CTA" → Quiz Results Page → Plans page
 * - Hero Quiz → Quiz → Quiz Results Page → (⭐Promo Page) → Plans page
 * - Hero Quiz → Quiz → Quiz Results Page → drop off → "Finish Sign Up CTA" → (⭐Promo Page) → Quiz Results Page → Plans page
 * - Hero Quiz → Quiz → Quiz Results Page → (⭐Promo Page) → drop off → "Finish Sign Up CTA" → (⭐Promo Page) → Quiz Results Page → Plans page
 */
export const usePrimaryCta = () => {
  const { isPromotionOn } = usePromotion()
  const cookieRedirectHref = useCookieRedirectHref()

  const { pathname, annualPass, primaryCtaOverride } = useStaticData<{
    pathname?: string
    annualPass?: Product | null
    primaryCtaOverride?: {
      href: string
      trackingProps?: Record<string, unknown>
    } | null
  }>()

  const isQuizResultsPage = pathname === paths.classRecommendationsResults

  const ctaHref =
    cookieRedirectHref ||
    primaryCtaOverride?.href ||
    (isPromotionOn ? paths.promotion : paths.classRecommendations)

  const ctaCopy = useMemo(() => {
    // "Finish Sign Up" CTA for users with saved quiz state
    if (cookieRedirectHref) return tf('cta.finishSignup')

    // Use "Sign Up" for Quiz Results page, "Get MasterClass" for all other pages
    if (isQuizResultsPage) return tf('cta.signup')

    return tf('cta.getMasterclass')
  }, [cookieRedirectHref, isQuizResultsPage])

  const hasCompletedQuiz = Boolean(cookieRedirectHref)

  const appliedOverride =
    !cookieRedirectHref && primaryCtaOverride?.href ? primaryCtaOverride : null

  const trackPrimaryCtaPurchaseClick = useCallback(
    ({ action, ...rest }: Partial<PurchaseClickProps>) => {
      const overrideTrackingProps =
        appliedOverride && rest.href === appliedOverride.href
          ? appliedOverride.trackingProps
          : undefined

      const props: PurchaseClickProps = {
        ...rest,
        action: action ?? ctaCopy,
        location: rest.location ?? 'primary_cta',
        annualPass: rest.annualPass ?? annualPass,
        ...(overrideTrackingProps && {
          purchaseClickProps: {
            ...rest.purchaseClickProps,
            ...overrideTrackingProps,
          },
        }),
      }

      trackGoldStandardPurchaseClick(props)
    },
    [ctaCopy, annualPass, appliedOverride],
  )

  return {
    ctaCopy,
    ctaHref,
    hasCompletedQuiz,
    isQuizResultsPage,
    trackPurchaseClick: trackPrimaryCtaPurchaseClick,
  }
}
